import { useMemo } from 'react';

/**
 * Hook personnalisé pour générer les styles de React Select
 * avec prise en charge du mode sombre
 * @param {Object} customStyles - Styles personnalisés optionnels pour écraser les valeurs par défaut
 * @returns {Object} Styles configurés pour React Select
 */
export const useSelectStyles = (customStyles = {}) => {
  return useMemo(() => {
    // Détecter si nous sommes en mode sombre
    const isDarkMode = document.documentElement.classList.contains('dark');

    // Styles de base avec support du mode sombre
    const baseStyles = {
      control: (provided, state) => ({
        ...provided,
        backgroundColor: isDarkMode ? '#374151' : provided.backgroundColor,
        borderColor: isDarkMode ? '#4B5563' : provided.borderColor,
        boxShadow: state.isFocused
          ? isDarkMode ? '0 0 0 1px #3B82F6' : provided.boxShadow
          : provided.boxShadow,
        '&:hover': {
          borderColor: isDarkMode ? '#6B7280' : provided.borderColor,
        },
        minHeight: '38px',
        height: '38px'
      }),
      valueContainer: (provided) => ({
        ...provided,
        padding: '0 8px',
        height: '38px'
      }),
      menu: (provided) => ({
        ...provided,
        backgroundColor: isDarkMode ? '#1F2937' : provided.backgroundColor,
        zIndex: 9999
      }),
      option: (provided, state) => ({
        ...provided,
        backgroundColor: isDarkMode
          ? state.isSelected
            ? '#3B82F6'
            : state.isFocused
              ? '#374151'
              : '#1F2937'
          : state.isSelected
            ? provided.backgroundColor
            : state.isFocused
              ? provided.backgroundColor
              : provided.backgroundColor,
        color: isDarkMode
          ? state.isSelected
            ? 'white'
            : '#E5E7EB'
          : provided.color,
        ':active': {
          backgroundColor: isDarkMode
            ? state.isSelected ? '#3B82F6' : '#4B5563'
            : provided[':active']?.backgroundColor
        }
      }),
      singleValue: (provided) => ({
        ...provided,
        color: isDarkMode ? '#E5E7EB' : provided.color
      }),
      input: (provided) => ({
        ...provided,
        color: isDarkMode ? '#E5E7EB' : provided.color,
        margin: 0,
        padding: 0
      }),
      placeholder: (provided) => ({
        ...provided,
        color: isDarkMode ? '#9CA3AF' : provided.color
      }),
      indicatorSeparator: (provided) => ({
        ...provided,
        backgroundColor: isDarkMode ? '#4B5563' : provided.backgroundColor,
        margin: '4px 0'
      }),
      dropdownIndicator: (provided) => ({
        ...provided,
        color: isDarkMode ? '#9CA3AF' : provided.color,
        padding: '0 8px',
        '&:hover': {
          color: isDarkMode ? '#D1D5DB' : provided['&:hover']?.color
        }
      }),
      clearIndicator: (provided) => ({
        ...provided,
        padding: '0 8px'
      }),
      menuPortal: (base) => ({
        ...base,
        zIndex: 9999
      })
    };

    // Fusionner avec les styles personnalisés fournis en paramètre
    const mergedStyles = { ...baseStyles };

    // Pour chaque clé dans customStyles, vérifier si elle existe déjà et la merger si nécessaire
    Object.keys(customStyles).forEach(key => {
      if (typeof customStyles[key] === 'function' && typeof baseStyles[key] === 'function') {
        // Cas où les deux sont des fonctions, wrapper la fonction de base
        mergedStyles[key] = (...args) => {
          const baseResult = baseStyles[key](...args);
          const customResult = customStyles[key](...args);
          return { ...baseResult, ...customResult };
        };
      } else {
        // Cas simple où on remplace complètement
        mergedStyles[key] = customStyles[key];
      }
    });

    return mergedStyles;
  }, [customStyles]);
};

/**
 * Version non-hook des styles React Select (pour les cas où les hooks ne peuvent pas être utilisés)
 * @param {boolean} isDarkMode - Indique si le mode sombre est actif
 * @param {Object} customStyles - Styles personnalisés optionnels
 * @returns {Object} Styles configurés pour React Select
 */
export const getSelectStyles = (isDarkMode = false, customStyles = {}) => {
  // Même logique que dans le hook, mais sans useMemo
  // ...code similaire mais sans hook...

  const baseStyles = {
    control: (provided, state) => ({
      ...provided,
      backgroundColor: isDarkMode ? '#374151' : provided.backgroundColor,
      // Le reste est identique à la version hook
    }),
    // ...autres styles identiques à la version hook
  };

  // Fusion des styles personnalisés (même logique que dans le hook)
  // ...

  return baseStyles;
};